<template>
  <div class="actor-search">
    <DsInput
      is-simplified
      v-model="searchQuery"
      placeholder="Search for actors…"
      @update:model-value="fetchActorsDebounced"
      class="limited-width"
      v-if="!actor || !actor.id"
    />

    <div class="search-indicator" v-if="searching">
      <icon name="spinner" style="vertical-align: middle;"/>
      Searching...
    </div>
    <div class="search-actor-list" v-else-if="actor && actor.id">
      <FormGroup is-simplified class="col-xs-12">
        <ActorBlock
          :actor="actor"
          mode="remove"
          :id="String(actor.id)"
          :name="actor.name"
          :description="selectedActorDescription"
          :url="actor.url"
          @remove="removeActor"
          :allow-input="true"
        />
      </FormGroup>
    </div>
    <div class="search-actor-list dropdown-search-actor-list" v-else-if="searchQuery">
      <ActorBlock
        class="search-actor-list-result"
        v-for="actor in notAddedActors"
        :key="actor.id"
        mode="add"
        :id="String(actor.id)"
        :name="actor.name"
        :description="actor.short_description"
        :url="actor.url"
        @add="addActor(actor)"
        :allow-input="true"
        :actor="actor"
      />
    </div>
  </div>
</template>
<script lang="ts">
  import { defineComponent } from 'vue'
  import { getMainActorTypeActorSuggestions } from '@/api/general-search'
  import { mapNewGlobalSearchResultToLegacyFormat } from '@/util/helpers'
  import debounce from 'lodash/debounce'
  import ActorBlock from '@/components/Dashboard/EmailReports/ActorBlock.vue'
  import ActorMixin from '@/Domain/Actor/Mixins/ActorMixin'
  import { htmlText } from '@/util/helpers'

  export default defineComponent({
    name: "ActorSearch",
    components: { ActorBlock },
    mixins: [ActorMixin],
    emits: ['set-actor', 'remove-actor'],
    props: {
      returnSqlId: {
        required: false,
        default: false,
      },
      modelValue: {
        type: Object,
        default: {}
      }
    },
    data() {
      return {
        searchQuery: '',
        lastUsedSearchQuery: '',
        searching: false,
        results: [],
        actor: [],
      }
    },
    computed: {
      user() {
        return this.$store.state.user.profile
      },
      notAddedActors() {
        return this.results
      },
      /**
       * ActorBlock prints the description as text, and an actor's own description is HTML — so
       * unstripped it showed the reader its `<p>` tags. The search results carry a plain
       * `short_description`; the selected actor only has the marked-up one.
       */
      selectedActorDescription() {
        const description = this.actor && (this.actor.description ?? this.actor.short_description)

        return htmlText(description).replace(/\s+/g, ' ').trim()
      },
    },
    methods: {
      fetchActors() {
        if (this.lastUsedSearchQuery === this.searchQuery || this.searchQuery.length <= 2) {
          return
        }

        this.lastUsedSearchQuery = this.searchQuery
        this.searching = true

        getMainActorTypeActorSuggestions(this.searchQuery)
          .then(result => {
            const combinedActorsWithoutDuplicates = mapNewGlobalSearchResultToLegacyFormat([...result.actors, ...result.products])

            this.results = combinedActorsWithoutDuplicates.slice(0, 10)
            this.searching = false
          })
          .catch(error => {
            this.searching = false
            throw error
          })
      },
      fetchActorsDebounced: debounce(function () {
        this.fetchActors()
      }, 500),
      addActor(actor) {
        if (!typeof actor === 'object') return
        this.actor = actor
        if (this.returnSqlId) {
          this.$emit('set-actor', actor.actor_id)
          return
        }
        this.$emit('set-actor', actor.id)
      },
      removeActor(id) {
        this.actor = null
        this.$emit('remove-actor', id)
      }
    },
    mounted() {
    },
    watch: {
      modelValue() {
        if (typeof this.modelValue === 'object') {
          this.actor = this.modelValue
          return
        }
      }
    }
  })
</script>
<style scoped lang="scss">

  .actor-search {
    position: relative;
  }

  .dropdown-search-actor-list {
    position: absolute;
    z-index: 100;
    background: white;
    height: 400px;
    overflow-x: scroll;
    border: 1px solid var(--primary-community-extra-lightest);
    border-radius: 0 0 0.5rem 0.5rem;
    top: 25px;
    border-top: 1px solid var(--primary-community-extra-lightest);

    .search-actor-list-result {
      cursor: pointer;
      padding: 0.5rem;
      margin: 0.5rem;
      border: 1px solid var(--primary-community-lightest);
      border-radius: 0.25rem;

      &:hover {
        background-color: var(--primary-community-lightest);
      }
    }
  }

  .search-actor-list {
    margin-top: 14px;
    border: 1px solid var(--primary-community);
    border-radius: 0.5rem;
  }
</style>
